import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';
import { useLocation } from 'react-router';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { GoogleOneTap } from '@sorare/core/src/components/GoogleOneTap';
import { ReportCampaignVisit } from '@sorare/core/src/components/ReportCampaignVisit';
import { ResolveImpactClickId } from '@sorare/core/src/components/ResolveImpactClickId';
import { MobileAppHelmet } from '@sorare/core/src/components/mobileApp/MobileAppHelmet';
import { NetworkInfo } from '@sorare/core/src/components/navigation/NetworkInfo';
import { RestrictedAccessModals } from '@sorare/core/src/components/restrictedAccess/RestrictedAccessModals';
import { CookieConsentBanner } from '@sorare/core/src/components/user/CookieConsentBanner';
import { WalletSetup } from '@sorare/core/src/components/wallet/WalletSetup';
import { IS_PRERENDER } from '@sorare/core/src/config';
import { AccountSecurityCheckProvider } from '@sorare/core/src/contexts/accountSecurityCheck/Provider';
import { AuthProvider } from '@sorare/core/src/contexts/auth/Provider';
import { ConfigProvider } from '@sorare/core/src/contexts/config/Provider';
import { ConnectionProvider } from '@sorare/core/src/contexts/connection/Provider';
import { CurrentUserProvider } from '@sorare/core/src/contexts/currentUser/Provider';
import { DialogOpenedProvider } from '@sorare/core/src/contexts/dialogOpened/Provider';
import { EventsProvider } from '@sorare/core/src/contexts/events/Provider';
import { HighlightProvider } from '@sorare/core/src/contexts/highlight/Provider';
import { OnboardingHighlightProvider } from '@sorare/core/src/contexts/onboardingHighlight/Provider';
import { OneTimeDialogProvider } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import { ResetPrivateKeyProvider } from '@sorare/core/src/contexts/resetPrivateKey/Provider';
import { RestrictedAccessProvider } from '@sorare/core/src/contexts/restrictedAccess/Provider';
import { SeoProvider } from '@sorare/core/src/contexts/seo/Provider';
import { SnackNotification } from '@sorare/core/src/contexts/snackNotification/Provider';
import { SportProvider } from '@sorare/core/src/contexts/sport/Provider';
import { SvgProvider } from '@sorare/core/src/contexts/svg/Provider';
import { TickerProvider } from '@sorare/core/src/contexts/ticker/Provider';
import { Frame as WalletFrame } from '@sorare/core/src/contexts/wallet/Frame';
import { WalletProvider } from '@sorare/core/src/contexts/wallet/Provider';
import { WalletDrawerProvider } from '@sorare/core/src/contexts/walletDrawer/Provider';
import { useSyncAuthStateAcrossTabs } from '@sorare/core/src/hooks/auth/useSyncAuthStateAcrossTabs';
import { useMeasureScrollbars } from '@sorare/core/src/hooks/useMeasureScrollbars';
import { useStyledScrollbar } from '@sorare/core/src/hooks/useStyledScrollbar';
import { useVh } from '@sorare/core/src/hooks/useVh';
import { logVersion } from '@sorare/core/src/lib/logVersion';
import { EnsureLatestAppVersion } from '@sorare/core/src/routing/EnsureLatestAppVersion';

import { MarketplaceProvider } from '@sorare/marketplace/src/contexts/Marketplace/Provider';
import { SingleSaleOfferProvider } from '@sorare/marketplace/src/contexts/singleSaleOffer/Provider';

import { DialogsProvider } from '@sorare/gameplay/src/contexts/dialogs';

const LocationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page(undefined);
    }
  }, [location.pathname]);

  return null;
};

export const AppProviders = ({ children }: { children: ReactNode }) => {
  useVh();
  useMeasureScrollbars();
  useStyledScrollbar();
  useSyncAuthStateAcrossTabs();
  useEffect(() => {
    logVersion();
  }, []);

  return (
    <>
      <LocationTracker />
      <MobileAppHelmet />
      <EnsureLatestAppVersion />
      <ReportCampaignVisit />
      <SnackNotification />
      <EventsProvider>
        <ResetPrivateKeyProvider>
          <DialogOpenedProvider>
            <HighlightProvider>
              <OnboardingHighlightProvider>
                <SportProvider>
                  <Suspense fallback={<LoadingIndicator fullScreen />}>
                    <ConfigProvider>
                      <SeoProvider>
                        <RestrictedAccessProvider>
                          <CurrentUserProvider>
                            <ResolveImpactClickId />
                            <AuthProvider>
                              <WalletDrawerProvider>
                                <WalletProvider>
                                  <ConnectionProvider>
                                    <GoogleOneTap />
                                    <AccountSecurityCheckProvider>
                                      <WalletSetup />
                                      <SingleSaleOfferProvider>
                                        <TickerProvider>
                                          <SvgProvider>
                                            <NetworkInfo />
                                            <RestrictedAccessModals />
                                            <OneTimeDialogProvider>
                                              <MarketplaceProvider>
                                                <DialogsProvider>
                                                  <Suspense fallback={null}>
                                                    {children}
                                                  </Suspense>
                                                  {
                                                    /* Do not load the Wallet Frame in prerender (google crawler, wayback machine, etc…) */
                                                    !IS_PRERENDER && (
                                                      <Suspense fallback={null}>
                                                        <WalletFrame />
                                                      </Suspense>
                                                    )
                                                  }
                                                </DialogsProvider>
                                              </MarketplaceProvider>
                                            </OneTimeDialogProvider>
                                          </SvgProvider>
                                        </TickerProvider>
                                      </SingleSaleOfferProvider>
                                    </AccountSecurityCheckProvider>
                                  </ConnectionProvider>
                                </WalletProvider>
                              </WalletDrawerProvider>
                            </AuthProvider>
                          </CurrentUserProvider>
                        </RestrictedAccessProvider>
                      </SeoProvider>
                    </ConfigProvider>
                  </Suspense>
                </SportProvider>
              </OnboardingHighlightProvider>
            </HighlightProvider>
          </DialogOpenedProvider>
          <CookieConsentBanner />
        </ResetPrivateKeyProvider>
      </EventsProvider>
    </>
  );
};
